import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import ProductCard from '@/components/ProductCard';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X } from 'lucide-react';

const CATEGORIES = ['Women', 'Men', 'Handbags', 'Watches', 'Jewelry', 'Shoes', 'Accessories'];
const CONDITIONS = ['Pristine', 'Excellent', 'Very Good', 'Good', 'Fair'];

export default function Shop() {
  const { t, api } = useApp();
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [designers, setDesigners] = useState([]);
  const [sort, setSort] = useState(params.get('sort') || 'newest');
  const [price, setPrice] = useState([0, 50000]);

  const filters = useMemo(() => ({
    category: params.get('category') || '',
    designer: params.get('designer') || '',
    condition: params.get('condition') || '',
    q: params.get('q') || '',
    min_price: params.get('min_price') || '',
    max_price: params.get('max_price') || '',
  }), [params]);

  useEffect(() => { api.get('/designers').then((r) => setDesigners(r.data || [])); }, [api]);

  useEffect(() => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    qs.set('sort', sort);
    qs.set('limit', '60');
    api.get(`/products?${qs.toString()}`).then((r) => {
      setProducts(r.data.items || []);
      setTotal(r.data.total || 0);
    });
  }, [api, filters, sort]);

  const updateFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const applyPrice = () => {
    const next = new URLSearchParams(params);
    next.set('min_price', price[0]);
    next.set('max_price', price[1]);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());

  const activeChips = Object.entries(filters).filter(([, v]) => !!v);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="shop-page">
      <div className="flex items-end justify-between mb-10 border-b border-border pb-6">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
            {filters.category || filters.designer || filters.q || 'All Pieces'}
          </h1>
          <p className="overline text-muted-foreground mt-3" data-testid="results-count">{total} {t('shop.results')}</p>
        </div>
        <div className="hidden sm:block">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-56 border-0 border-b border-foreground rounded-none" data-testid="sort-trigger">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">{t('shop.newest')}</SelectItem>
              <SelectItem value="price_asc">{t('shop.priceLow')}</SelectItem>
              <SelectItem value="price_desc">{t('shop.priceHigh')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="active-filters">
          {activeChips.map(([k, v]) => (
            <button key={k} onClick={() => updateFilter(k, '')} className="overline border border-foreground px-3 py-1.5 inline-flex items-center gap-2 hover:bg-foreground hover:text-background transition">
              {v} <X size={12} />
            </button>
          ))}
          <button onClick={clearAll} className="overline text-accent link-underline" data-testid="clear-all">{t('shop.clear')}</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <aside className="lg:col-span-3 space-y-10" data-testid="filters-panel">
          <div>
            <div className="overline mb-4">{t('shop.category')}</div>
            <ul className="space-y-2">
              {CATEGORIES.map((c) => (
                <li key={c}>
                  <button onClick={() => updateFilter('category', filters.category === c ? '' : c)} className={`text-sm font-light link-underline ${filters.category === c ? 'text-accent' : ''}`} data-testid={`filter-cat-${c}`}>{c}</button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-4">{t('shop.designer')}</div>
            <ul className="space-y-2 max-h-64 overflow-y-auto pr-2">
              {designers.map((d) => (
                <li key={d.name}>
                  <button onClick={() => updateFilter('designer', filters.designer === d.name ? '' : d.name)} className={`text-sm font-light link-underline flex w-full justify-between ${filters.designer === d.name ? 'text-accent' : ''}`} data-testid={`filter-des-${d.name}`}>
                    <span>{d.name}</span>
                    <span className="text-muted-foreground text-xs">{d.count}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-4">{t('shop.condition')}</div>
            <ul className="space-y-3">
              {CONDITIONS.map((c) => (
                <li key={c} className="flex items-center gap-3">
                  <Checkbox id={`cond-${c}`} checked={filters.condition === c} onCheckedChange={(v) => updateFilter('condition', v ? c : '')} data-testid={`filter-cond-${c}`} />
                  <label htmlFor={`cond-${c}`} className="text-sm font-light cursor-pointer">{c}</label>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline mb-4">{t('shop.price')}</div>
            <Slider value={price} onValueChange={setPrice} min={0} max={50000} step={100} data-testid="price-slider" />
            <div className="flex justify-between mt-3 text-xs font-light text-muted-foreground">
              <span>${price[0].toLocaleString()}</span>
              <span>${price[1].toLocaleString()}+</span>
            </div>
            <button onClick={applyPrice} className="mt-4 overline link-underline" data-testid="apply-price">Apply</button>
          </div>
        </aside>

        <div className="lg:col-span-9">
          {products.length === 0 ? (
            <p className="font-light text-muted-foreground" data-testid="no-results">No items match your filters.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-12" data-testid="product-grid">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
